import { dateOf } from "@bd/sim";
import { Icon, Sheet } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { currentLang, t, tdate, tx } from "../i18n";

/** Every Ukrainian dictionary, to tell a written-out detail ("A great game, then they go") from an effect list. */
const UK = Object.assign({}, ...Object.values(import.meta.glob("../i18n/uk/*.json", { eager: true, import: "default" }))) as Record<string, string>;

const MONEY = /^[+−-]?\$[\d,.]+[KM]?$/;
const CHANCE = /(\d+)% chance: /;
const OTHERWISE = "; otherwise ";
const lowFirst = (s: string) => s.replace(/^\p{Lu}(?!\p{Lu})/u, (c) => c.toLowerCase());
const upFirst = (s: string) => s.replace(/^\p{Ll}/u, (c) => c.toUpperCase());

/**
 * The engine's effect list (effectSummary in packages/sim/src/director.ts): pieces joined with ", ", the first one
 * capitalised, and at most one "N% chance: …; otherwise …" whose two sides are lists of their own.
 * Each piece is translated on its own (dictionary keys are the lower-case form) and the list is put back together.
 */
function effectsText(s: string): string {
  const m = CHANCE.exec(s);
  const head = m ? s.slice(0, m.index).replace(/, $/, "") : s;
  const parts = head ? head.split(", ").map((p) => (MONEY.test(p) ? p : tx(lowFirst(p)))) : [];
  if (m) {
    const rest = s.slice(m.index + m[0].length);
    const cut = rest.indexOf(OTHERWISE);
    const yes = effectsText(cut < 0 ? rest : rest.slice(0, cut));
    const no = cut < 0 ? "nothing" : rest.slice(cut + OTHERWISE.length);
    parts.push(no === "nothing"
      ? t("{pc}% chance: {yes}; otherwise nothing", { pc: m[1], yes })
      : t("{pc}% chance: {yes}; otherwise {no}", { pc: m[1], yes, no: effectsText(no) }));
  }
  return parts.join(", ");
}

/** The line under an option, in the player's language. Events and story steps write effect lists; the rest is prose. */
function detailText(kind: string, s: string): string {
  if (currentLang() === "en" || !s) return s;
  if (Object.hasOwn(UK, s) || (kind !== "event" && kind !== "arc")) return tx(s);
  const out = effectsText(s);
  return /^\p{Lu}/u.test(s) ? upFirst(out) : out;
}

/** An effect that is a plain number of something with an icon: "+$4,000", "−20 fans", "+3 reputation", "+10 morale", "+40 research". */
const ICON_FACTS: [RegExp, string, string][] = [
  [/^([+−-]?\$[\d,.]+[KM]?)$/, "coin", "var(--gold)"],
  [/^([+−-][\d,.]+[KM]?) fans$/, "fans", "var(--coral)"],
  [/^([+−-][\d,.]+) reputation$/, "star", "var(--gold)"],
  [/^([+−-][\d,.]+) morale$/, "heart", "var(--coral)"],
  [/^([+−-][\d,.]+) research$/, "research", "#5a8cdc"],
];

/**
 * An option's effects as short icon + value pieces (coin −$1,400 · fans +20); anything that isn't a plain number stays
 * as words. Only events and story steps write effect lists; other choices write a line of prose, shown as it is.
 */
function OptionFacts({ kind, detail }: { kind: string; detail: string }) {
  if (kind !== "event" && kind !== "arc") return <span className="opt-text">{detailText(kind, detail)}</span>;
  // the chance part ("40% chance: …; otherwise …") always comes last, as one piece
  const m = CHANCE.exec(detail);
  const head = m ? detail.slice(0, m.index).replace(/, $/, "") : detail;
  const pieces = head ? head.split(", ") : [];
  const rest = m ? detail.slice(m.index) : "";
  const words: string[] = [];
  const chips: { icon: string; color: string; v: string; title: string }[] = [];
  for (const p of pieces) {
    const low = lowFirst(p);
    const hit = ICON_FACTS.map(([re, icon, color]) => ({ m: re.exec(low), icon, color })).find((x) => x.m);
    if (hit?.m) chips.push({ icon: hit.icon, color: hit.color, v: hit.m[1], title: detailText(kind, p) });
    else words.push(p);
  }
  if (rest) words.push(rest);
  const text = words.length ? detailText(kind, upFirst(words.join(", "))) : "";
  return (
    <span className="ichips">
      {chips.map((c, i) => <span key={i} className={`ichip ${/^[−-]/.test(c.v) ? "bad" : /^\+/.test(c.v) ? "good" : ""}`} title={c.title}><Icon name={c.icon} size={14} color={c.color} />{c.v}</span>)}
      {text && <span className="opt-text">{text}</span>}
    </span>
  );
}

/** Any choice the world puts to you (poaching offers now; events and story arcs later). */
export function Decision({ id }: { id: string }) {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const co = me(game)!;
  const d = co.pending.find((x) => x.id === id);
  if (!d) return null;
  const choose = async (opt: string) => {
    await cmd({ type: "decide", decisionId: d.id, option: opt });
    set({ screen: null });
  };
  return (
    <Sheet title={tx(d.title)} badge={<span className="ichip muted" title={t("Decide by {date}", { date: tdate(dateOf(d.expiresWeek).label) })}><Icon name="calendar" size={14} />{tdate(dateOf(d.expiresWeek).label)}</span>} testId="decision">
      <p style={{ marginTop: 0, fontSize: "1.05rem", lineHeight: 1.5 }}>{tx(d.text)}</p>
      <div className="stack">
        {d.options.map((o, i) => (
          // no option is styled as the "right" one unless it's the only one; the detail stays readable on either style
          <button key={o.id} className={`btn decision-opt ${d.options.length === 1 ? "primary" : ""}`} onClick={() => choose(o.id)} data-testid="decision-option" data-index={i}>
            <span>{tx(o.label)}</span>
            {o.detail ? <OptionFacts kind={d.kind} detail={o.detail} /> : null}
          </button>
        ))}
      </div>
    </Sheet>
  );
}
